import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import QRCode from "qrcode";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { WHAT_LOGO_PNG_BASE64 } from "@/lib/email-assets/what-logo";

const LOGO_CID = "what-logo";
const REPLY_TO = process.env.EMAIL_REPLY_TO || "info@whatfinland.com";
const EMAIL_FROM = "What Finland <info@whatfinland.com>";
const BRAND_URL = "https://whatfinland.com";

function appUrl() {
  return process.env.APP_URL || "https://whatfinland.com";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function randomToken(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function shortId(id: string): string {
  return id.replace(/-/g, "").slice(0, 8).toUpperCase();
}

function formatDateLong(d: string): string {
  // d is YYYY-MM-DD
  const [y, m, day] = d.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, day));
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ============================================================
// PUBLIC: list products
// ============================================================
const PRODUCT_SELECT =
  "id, slug, operator, name, subtitle, description, price_cents, currency, image_url, badge, duration_label, highlights, affiliate_url, payment_url, payment_method, age_tiers, sort_order";

export const listTicketProducts = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("ticket_products")
    .select(PRODUCT_SELECT)
    .eq("active", true)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return { products: data ?? [] };
});

export const getTicketProductBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(120) }).parse(input))
  .handler(async ({ data }) => {
    const { data: product, error } = await supabaseAdmin
      .from("ticket_products")
      .select(PRODUCT_SELECT)
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { product };
  });

// ============================================================
// ADMIN: ticket products CRUD
// ============================================================
async function assertAdmin(supabase: any, userId: string) {
  const { assertIsAdmin } = await import("@/lib/assert-admin.server");
  await assertIsAdmin(supabase, userId);
}

export const adminListTicketProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await supabaseAdmin
      .from("ticket_products")
      .select(`${PRODUCT_SELECT}, active`)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return { products: data ?? [] };
  });

const paymentMethodSchema = z.enum(["stripe", "manual", "partner", "free"]);

const ageTierSchema = z.object({
  label: z.string().trim().min(1).max(60),
  min_age: z.number().int().min(0).max(120),
  max_age: z.number().int().min(0).max(120),
  price_cents: z.number().int().min(0).max(1_000_000),
  payment_url: z.string().trim().max(2048).nullable().optional().or(z.literal("")),
  payment_method: paymentMethodSchema.optional(),
});

const upsertProductSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().min(1).max(120).regex(/^[^\s/?#]+$/, "no spaces or / ? # characters"),
  operator: z.enum(["royalline", "citytour", "combo"]),
  name: z.string().trim().min(1).max(200),
  subtitle: z.string().trim().max(200).nullable().optional(),
  description: z.string().trim().max(2000).default(""),
  price_cents: z.number().int().min(0).max(1_000_000),
  currency: z.string().trim().min(3).max(8).default("EUR"),
  image_url: z.string().trim().max(2048).nullable().optional(),
  badge: z.string().trim().max(40).nullable().optional(),
  duration_label: z.string().trim().max(80).nullable().optional(),
  highlights: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
  affiliate_url: z.string().trim().max(2048).nullable().optional().or(z.literal("")),
  payment_url: z.string().trim().max(2048).nullable().optional().or(z.literal("")),
  payment_method: paymentMethodSchema.default("partner"),
  age_tiers: z.array(ageTierSchema).max(10).default([]),
  active: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(10_000).default(0),
});

export const adminUpsertTicketProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => upsertProductSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      slug: data.slug,
      operator: data.operator,
      name: data.name,
      subtitle: data.subtitle || null,
      description: data.description || "",
      price_cents: data.price_cents,
      currency: data.currency,
      image_url: data.image_url || null,
      badge: data.badge || null,
      duration_label: data.duration_label || null,
      highlights: data.highlights ?? [],
      affiliate_url: data.affiliate_url ? data.affiliate_url : null,
      payment_url: data.payment_url ? data.payment_url : null,
      payment_method: data.payment_method,
      age_tiers: (data.age_tiers ?? []).map((t) => ({
        label: t.label,
        min_age: t.min_age,
        max_age: t.max_age,
        price_cents: t.price_cents,
        payment_url: t.payment_url || null,
        payment_method: t.payment_method ?? "partner",
      })),
      active: data.active,
      sort_order: data.sort_order,
    };

    if (data.id) {
      const { error } = await supabaseAdmin
        .from("ticket_products")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      const { translateRowNow } = await import("@/lib/translate-now.server");
      await translateRowNow("ticket_product", data.id);
      return { ok: true, id: data.id };
    } else {
      const { data: row, error } = await supabaseAdmin
        .from("ticket_products")
        .insert(payload)
        .select("id")
        .single();
      if (error || !row) throw new Error(error?.message || "could not create");
      const { translateRowNow } = await import("@/lib/translate-now.server");
      await translateRowNow("ticket_product", row.id);
      return { ok: true, id: row.id };
    }
  });

export const adminDeleteTicketProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await supabaseAdmin.from("ticket_products").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============================================================
// PUBLIC: look up a ticket by token (for scan page)
// ============================================================
export const getTicketByToken = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ token: z.string().min(8).max(128) }).parse(input))
  .handler(async ({ data }) => {
    const { data: ticket, error } = await supabaseAdmin
      .from("tickets")
      .select(
        "id, valid_date, holder_name, used_at, qr_token, order_id, product:ticket_products(name, operator, image_url, duration_label)",
      )
      .eq("qr_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!ticket) return { ticket: null };
    const { data: order } = await supabaseAdmin
      .from("ticket_orders")
      .select("status")
      .eq("id", (ticket as any).order_id)
      .maybeSingle();
    if (order?.status !== "confirmed") return { ticket: null };
    return {
      ticket: {
        shortId: shortId(ticket.id as string),
        valid_date: ticket.valid_date as string,
        holder_name: ticket.holder_name as string,
        used_at: ticket.used_at as string | null,
        product_name: (ticket.product as any)?.name ?? "ticket",
        operator: (ticket.product as any)?.operator ?? null,
        image_url: (ticket.product as any)?.image_url ?? null,
        duration_label: (ticket.product as any)?.duration_label ?? null,
      },
    };
  });

// ============================================================
// PUBLIC: get an order + its tickets (confirmation page)
// Returns tickets keyed by token (no auth required — fresh after checkout)
// ============================================================
export const getOrderForConfirmation = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const { data: order, error: orderErr } = await supabaseAdmin
      .from("ticket_orders")
      // buyer_email intentionally omitted — confirmation links may end up in
      // browser history / referrer headers / analytics, so we don't return PII
      // to anyone holding the order UUID.
      .select("id, buyer_name, total_cents, currency, status, created_at")
      .eq("id", data.orderId)
      .maybeSingle();
    if (orderErr) throw new Error(orderErr.message);
    if (!order) return { order: null, tickets: [] };

    const paid = order.status === "confirmed";

    const { data: tickets, error: tErr } = await supabaseAdmin
      .from("tickets")
      .select(
        "id, valid_date, holder_name, qr_token, used_at, product:ticket_products(name, operator)",
      )
      .eq("order_id", data.orderId)
      .order("created_at", { ascending: true });
    if (tErr) throw new Error(tErr.message);

    return {
      order,
      tickets: (tickets ?? []).map((t: any) => ({
        id: t.id as string,
        shortId: shortId(t.id as string),
        valid_date: t.valid_date as string,
        holder_name: t.holder_name as string,
        qr_token: paid ? (t.qr_token as string) : "",
        used_at: t.used_at as string | null,
        product_name: t.product?.name ?? "ticket",
        operator: t.product?.operator ?? null,
      })),
    };
  });

// ============================================================
// CREATE ORDER (public — no auth required)
// ============================================================
const createOrderSchema = z.object({
  productSlug: z.string().min(1).max(120),
  validDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date"),
  buyerName: z.string().trim().min(1).max(120),
  buyerEmail: z.string().trim().email().max(255),
  holderNames: z.array(z.string().trim().min(1).max(120)).min(1).max(20),
});

export const createTicketOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => createOrderSchema.parse(input))
  .handler(async ({ data, context }) => {
    // Date must be today or later (Europe/Helsinki ≈ +2/+3, but day-level comparison via UTC is fine
    // because dates are stored as DATE; we accept anything from yesterday-UTC forward.)
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    if (data.validDate < todayStr) {
      throw new Error("Date must be today or later");
    }

    // Load product
    const { data: product, error: prodErr } = await supabaseAdmin
      .from("ticket_products")
      .select("id, slug, name, price_cents, currency, operator")
      .eq("slug", data.productSlug)
      .eq("active", true)
      .maybeSingle();
    if (prodErr) throw new Error(prodErr.message);
    if (!product) throw new Error("Product not found");

    const totalCents = product.price_cents * data.holderNames.length;

    // NOTE: orders are inserted as "pending" — payment is handled out-of-band
    // (external operator payment URL). A trusted webhook/admin action must
    // flip status to "confirmed" before tickets are valid for entry.
    const { data: order, error: orderErr } = await supabaseAdmin
      .from("ticket_orders")
      .insert({
        user_id: context.userId,
        buyer_name: data.buyerName,
        buyer_email: data.buyerEmail.toLowerCase(),
        total_cents: totalCents,
        currency: product.currency,
        status: "pending",
      })
      .select("id, created_at")
      .single();
    if (orderErr || !order) throw new Error(orderErr?.message || "Could not create order");

    // Create one ticket per holder
    const ticketRows = data.holderNames.map((name) => ({
      order_id: order.id,
      product_id: product.id,
      valid_date: data.validDate,
      holder_name: name,
      qr_token: randomToken(),
    }));

    const { data: insertedTickets, error: tErr } = await supabaseAdmin
      .from("tickets")
      .insert(ticketRows)
      .select("id, qr_token, holder_name");
    if (tErr || !insertedTickets) throw new Error(tErr?.message || "Could not create tickets");

    // Notify the buyer in their profile inbox
    try {
      const { notifyUsers } = await import("@/lib/notify.server");
      await notifyUsers([context.userId], {
        title: `Order received: ${product.name}`,
        body: `${data.holderNames.length} ticket(s) for ${data.validDate}. We'll confirm once payment is verified.`,
        link: "/profile?tab=bookings",
        kind: "ticket",
      });
    } catch (e) {
      console.error("[tickets] notifyUsers failed:", e);
    }

    // Do not email QR codes until the order is confirmed (payment / admin).
    return { orderId: order.id, emailSent: false, emailError: null };
  });

// ============================================================
// ADMIN: mark ticket as used / unused
// ============================================================
export const markTicketUsed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      ticketId: z.string().uuid(),
      used: z.boolean(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);

    const { error } = await supabaseAdmin
      .from("tickets")
      .update({ used_at: data.used ? new Date().toISOString() : null })
      .eq("id", data.ticketId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============================================================
// ADMIN: confirm a paid order — flips status and emails the QR tickets
// ============================================================
export const confirmTicketOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);

    const { data: order, error: oErr } = await supabaseAdmin
      .from("ticket_orders")
      .select("id, buyer_name, buyer_email, status, user_id")
      .eq("id", data.orderId)
      .maybeSingle();
    if (oErr) throw new Error(oErr.message);
    if (!order) throw new Error("Order not found");

    if (order.status !== "confirmed") {
      const { error: uErr } = await supabaseAdmin
        .from("ticket_orders")
        .update({ status: "confirmed" })
        .eq("id", order.id);
      if (uErr) throw new Error(uErr.message);
    }

    const { data: tickets, error: tErr } = await supabaseAdmin
      .from("tickets")
      .select("id, qr_token, holder_name, valid_date, product:ticket_products(name, operator)")
      .eq("order_id", order.id);
    if (tErr) throw new Error(tErr.message);
    if (!tickets || tickets.length === 0) throw new Error("Order has no tickets");

    const first: any = tickets[0];

    // Notify the buyer in their profile inbox
    if ((order as any).user_id) {
      try {
        const { notifyUsers } = await import("@/lib/notify.server");
        await notifyUsers([(order as any).user_id as string], {
          title: `Tickets confirmed: ${first.product?.name ?? "your order"}`,
          body: `${tickets.length} ticket(s) for ${first.valid_date}. Your QR codes are ready.`,
          link: "/profile?tab=bookings",
          kind: "ticket",
        });
      } catch (e) {
        console.error("[tickets] notifyUsers failed:", e);
      }
    }

    try {
      await sendTicketEmail({
        to: order.buyer_email,
        buyerName: order.buyer_name,
        productName: first.product?.name ?? "Ticket",
        operator: first.product?.operator ?? "",
        validDate: first.valid_date,
        orderId: order.id,
        tickets: tickets.map((t: any) => ({
          shortId: shortId(t.id),
          holderName: t.holder_name,
          token: t.qr_token,
        })),
      });
      return { ok: true, emailSent: true, emailError: null as string | null };
    } catch (e) {
      return { ok: true, emailSent: false, emailError: e instanceof Error ? e.message : "Email failed" };
    }
  });



// ============================================================
// ADMIN: list orders
// ============================================================
export const adminListOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);

    const { data: orders, error } = await supabaseAdmin
      .from("ticket_orders")
      .select("id, buyer_name, buyer_email, total_cents, currency, status, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);

    const orderIds = (orders ?? []).map((o: any) => o.id);
    let tickets: any[] = [];
    if (orderIds.length > 0) {
      const { data: t, error: tErr } = await supabaseAdmin
        .from("tickets")
        .select("id, order_id, valid_date, holder_name, qr_token, used_at, product:ticket_products(name, operator)")
        .in("order_id", orderIds);
      if (tErr) throw new Error(tErr.message);
      tickets = t ?? [];
    }

    return {
      orders: (orders ?? []).map((o: any) => ({
        ...o,
        tickets: tickets
          .filter((t) => t.order_id === o.id)
          .map((t) => ({
            id: t.id,
            shortId: shortId(t.id),
            valid_date: t.valid_date,
            holder_name: t.holder_name,
            qr_token: t.qr_token,
            used_at: t.used_at,
            product_name: t.product?.name ?? "ticket",
            operator: t.product?.operator ?? null,
          })),
      })),
    };
  });

// ============================================================
// USER: list my ticket orders (matched by user_id OR buyer_email)
// ============================================================
export const listMyTicketOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId, claims } = context;
    const email = ((claims as any)?.email as string | undefined)?.toLowerCase() ?? null;

    let query = supabaseAdmin
      .from("ticket_orders")
      .select("id, buyer_name, buyer_email, total_cents, currency, status, created_at, user_id")
      .order("created_at", { ascending: false })
      .limit(100);

    if (email) {
      query = query.or(`user_id.eq.${userId},buyer_email.eq.${email}`);
    } else {
      query = query.eq("user_id", userId);
    }

    const { data: orders, error } = await query;
    if (error) throw new Error(error.message);

    const orderIds = (orders ?? []).map((o: any) => o.id);
    let tickets: any[] = [];
    if (orderIds.length > 0) {
      const { data: t, error: tErr } = await supabaseAdmin
        .from("tickets")
        .select("id, order_id, valid_date, holder_name, qr_token, used_at, product:ticket_products(name, operator, image_url)")
        .in("order_id", orderIds)
        .order("created_at", { ascending: true });
      if (tErr) throw new Error(tErr.message);
      tickets = t ?? [];
    }

    return {
      orders: (orders ?? []).map((o: any) => ({
        id: o.id,
        buyer_name: o.buyer_name,
        buyer_email: o.buyer_email,
        total_cents: o.total_cents,
        currency: o.currency,
        status: o.status,
        created_at: o.created_at,
        tickets: tickets
          .filter((t) => t.order_id === o.id)
          .map((t) => ({
            id: t.id,
            shortId: shortId(t.id),
            valid_date: t.valid_date,
            holder_name: t.holder_name,
            qr_token: o.status === "confirmed" ? t.qr_token : "",
            used_at: t.used_at,
            product_name: t.product?.name ?? "ticket",
            operator: t.product?.operator ?? null,
            image_url: t.product?.image_url ?? null,
          })),
      })),
    };
  });



// ============================================================
// EMAIL HELPERS
// ============================================================
function operatorLabel(op: string): string {
  if (op === "royalline") return "Royalline.fi";
  if (op === "citytour") return "Citytour.fi";
  if (op === "combo") return "Royalline.fi + Citytour.fi";
  return op;
}

async function sendTicketEmail(params: {
  to: string;
  buyerName: string;
  productName: string;
  operator: string;
  validDate: string;
  orderId: string;
  tickets: Array<{ shortId: string; holderName: string; token: string }>;
}) {
  const resendKey = process.env.RESEND_API_KEY;
  if (!resendKey) throw new Error("RESEND_API_KEY not configured");

  const from = EMAIL_FROM;
  const dateLabel = formatDateLong(params.validDate);

  // Generate QR for each ticket as inline base64 PNG
  const qrAttachments: Array<{
    filename: string;
    content: string;
    content_id: string;
    content_type: string;
    disposition: string;
  }> = [
    {
      filename: "what-logo.png",
      content: WHAT_LOGO_PNG_BASE64,
      content_id: LOGO_CID,
      content_type: "image/png",
      disposition: "inline",
    },
  ];

  const ticketBlocks: string[] = [];
  for (const t of params.tickets) {
    const ticketUrl = `${appUrl()}/ticket/${t.token}`;
    const qrDataUrl = await QRCode.toDataURL(ticketUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
    });
    const cid = `qr-${t.shortId}`;
    qrAttachments.push({
      filename: `ticket-${t.shortId}.png`,
      content: qrDataUrl.replace(/^data:image\/png;base64,/, ""),
      content_id: cid,
      content_type: "image/png",
      disposition: "inline",
    });

    ticketBlocks.push(`
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t-card" style="margin:18px 0;border:1px solid #e2e8f0;border-radius:18px;overflow:hidden;background:#ffffff;">
        <tr>
          <td style="padding:22px 24px 6px;border-bottom:1px solid #f1f5f9;">
            <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#64748b;font-weight:700;">${escapeHtml(operatorLabel(params.operator))}</div>
            <div style="margin-top:6px;font-size:18px;font-weight:800;color:#0b1d3a;line-height:1.25;">${escapeHtml(params.productName)}</div>
          </td>
        </tr>
        <tr>
          <td style="padding:18px 24px 6px;">
            <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#64748b;font-weight:700;">valid date</div>
            <div style="margin-top:6px;font-size:22px;font-weight:800;color:#0b1d3a;">${escapeHtml(dateLabel)}</div>
          </td>
        </tr>
        <tr>
          <td style="padding:14px 24px 0;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="top" style="padding-right:14px;">
                  <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#64748b;font-weight:700;">holder</div>
                  <div style="margin-top:6px;font-size:15px;font-weight:700;color:#0b1d3a;">${escapeHtml(t.holderName)}</div>
                  <div style="margin-top:14px;font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#64748b;font-weight:700;">ticket id</div>
                  <div style="margin-top:6px;font-family:'SFMono-Regular',Menlo,monospace;font-size:13px;letter-spacing:0.1em;font-weight:700;color:#0b1d3a;">${escapeHtml(t.shortId)}</div>
                </td>
                <td valign="top" align="right" width="140">
                  <img src="cid:${cid}" alt="QR code" width="120" height="120" style="display:block;width:120px;height:120px;border:1px solid #e2e8f0;border-radius:8px;" />
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:18px 24px 22px;">
            <a href="${ticketUrl}" style="display:inline-block;background:#0b1d3a;color:#ffffff;text-decoration:none;font-weight:700;font-size:13px;padding:11px 22px;border-radius:10px;">view ticket</a>
          </td>
        </tr>
      </table>
    `);
  }

  const html = `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>your tickets for ${escapeHtml(dateLabel)}</title>
<style>
  @media only screen and (max-width:600px){
    .t-wrap{padding:24px 10px !important;}
    .t-card{border-radius:16px !important;}
    .t-h1{font-size:22px !important;}
    .t-pad{padding:24px 20px !important;}
  }
</style>
</head>
<body style="margin:0;padding:0;background:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">your ${params.tickets.length === 1 ? "ticket" : "tickets"} for ${escapeHtml(dateLabel)} — show the QR at the gate.</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t-wrap" style="background:#ffffff;padding:40px 16px;">
  <tr><td align="center">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#ffffff;border-radius:24px;border:1px solid #eef1f7;overflow:hidden;">
      <tr><td class="t-pad" style="padding:32px 36px 20px;border-bottom:1px solid #eef1f7;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td valign="middle">
              <img src="cid:${LOGO_CID}" alt="what" width="110" height="40" style="display:block;height:40px;width:110px;border:0;" />
            </td>
            <td align="right" valign="middle" style="font-size:11px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:#94a3b8;">tickets</td>
          </tr>
        </table>
        <div style="display:inline-block;margin-top:18px;padding:6px 12px;background:#ecfdf5;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#065f46;">confirmed</div>
        <h1 class="t-h1" style="margin:14px 0 8px;font-size:26px;line-height:1.2;font-weight:800;color:#0b1d3a;letter-spacing:-0.4px;">your ${params.tickets.length === 1 ? "ticket is" : `${params.tickets.length} tickets are`} ready</h1>
        <p style="margin:0;font-size:14px;line-height:1.6;color:#475569;">valid on <strong style="color:#0b1d3a;">${escapeHtml(dateLabel)}</strong>. show the QR code at the gate — staff will scan it to admit you.</p>
      </td></tr>
      <tr><td class="t-pad" style="padding:8px 36px 12px;">
        ${ticketBlocks.join("")}
      </td></tr>
      <tr><td class="t-pad" style="padding:8px 36px 32px;">
        <hr style="border:none;border-top:1px solid #e2e8f0;margin:0 0 18px;"/>
        <p style="margin:0;font-size:13px;line-height:1.6;color:#64748b;">
          order <span style="font-family:'SFMono-Regular',Menlo,monospace;color:#0b1d3a;">${escapeHtml(shortId(params.orderId))}</span> · save this email — your tickets stay valid only for the date above.
        </p>
        <p style="margin:14px 0 0;font-size:12px;line-height:1.6;color:#94a3b8;">
          questions? email us at <a href="mailto:${REPLY_TO}" style="color:#1e3a8a;text-decoration:none;font-weight:600;">${REPLY_TO}</a>.<br/>
          sent by what finland · <a href="${BRAND_URL}" style="color:#1e3a8a;text-decoration:none;">${BRAND_URL.replace("https://", "")}</a>
        </p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;

  const subject = `your ${params.tickets.length === 1 ? "ticket" : "tickets"} for ${dateLabel}`;

  const res = await fetch("https://api.resend.com/emails", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${resendKey}`,
    },
    body: JSON.stringify({
      from,
      to: [params.to],
      reply_to: REPLY_TO,
      subject,
      html,
      attachments: qrAttachments,
    }),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Resend ${res.status}: ${body}`);
  }
}
